/**
 * Direct Upload Service
 * Uploads files directly to MinIO via presigned URLs (ADR-011).
 * Bypasses backend for chunk data transfer — only metadata goes through the API.
 */

import api, { isSessionExpiredError, lastRefreshTerminalCode, refreshAuthToken, startTokenKeepalive } from './api';
import type { ApiResponse } from '../types/api.types';
import { createSHA256 } from 'hash-wasm';
import { getAdaptiveConfig } from '../utils/adaptive-config';
import type { DedupStatus, UploadProgressStatus } from '../types/enums.generated';
import { beginUploadActivity } from '../stores/uploadActivityStore';
import {
  initUploadState,
  markChunkComplete as markChunkCompleteDB,
  removeUploadState,
} from '../db/upload-state-db';

// ============== Types ==============

/**
 * Progress shape handed to Files.tsx's per-file progress bar. It predates the
 * presigned flow (it was the chunked proxy's progress type, ADR-040 X-4 moved
 * it here when chunked-upload.service.ts was deleted); directUpload maps its
 * own DirectUploadProgress onto it in file.service.ts.
 */
export interface ChunkedUploadProgress {
  phase: 'hashing' | 'initializing' | 'uploading' | 'completing';
  overallProgress: number;
  chunkProgress?: {
    current: number;
    total: number;
    chunkIndex: number;
  };
  bytesUploaded: number;
  totalBytes: number;
  speed?: number; // bytes per second
  eta?: number; // seconds remaining
}

export interface DirectUploadProgress {
  phase: 'hashing' | 'initializing' | 'uploading' | 'processing' | 'completed' | 'failed';
  overallProgress: number; // 0-100
  hashProgress?: number;
  uploadProgress?: {
    totalChunks: number;
    uploadedChunks: number;
    uploadingChunks: number;
    bytesUploaded: number;
    speed: number;
    eta?: number;
  };
  processingProgress?: {
    totalChunks: number;
    completedChunks: number;
    failedChunks: number;
    failedChunkIndices: number[];
  };
}

/** ADR-054 D2: the upload is waiting for the network (since / deadline are epoch ms). */
export interface UploadPause {
  since: number;
  deadline: number;
}

interface DirectUploadOptions {
  chunkSize?: number; // Default 256MB
  maxConcurrent?: number; // Default 3
  onProgress?: (progress: DirectUploadProgress) => void;
  signal?: AbortSignal;
  /**
   * ADR-054 D2: how long to wait out a network outage before the upload
   * counts as failed — UPLOAD_RESUME_WINDOW from GET /auth/config
   * (upload_resume_window_seconds). Default 4h; 0 = no waiting (fail after
   * MAX_RETRIES, the pre-ADR-054 behaviour).
   */
  resumeWindowMs?: number;
  /** Called with the pause when the upload starts waiting for the network, and with null when it resumes. */
  onPauseChange?: (pause: UploadPause | null) => void;
}

interface DirectUploadResult {
  fileHash: string;
  fileName: string;
  fileSize: number;
  totalChunks: number;
  status: string;
}

interface HashResult {
  fileHash: string;
  chunkHashes: string[];
}

interface InitUploadResponse {
  total_chunks: number;
  chunk_size: number;
  status: string;
  dedup_status?: DedupStatus;
}

interface PresignedUrlResponse {
  presigned_url: string;
  object_key: string;
}

interface PresignedUrlBatchItem {
  chunk_index: number;
  presigned_url: string;
  object_key: string;
  expires_in: number;
}

interface PresignedUrlBatchResponse {
  urls: PresignedUrlBatchItem[];
}

interface ChunkCompleteResponse {
  chunk_status: string;
  task_id: string;
}

/**
 * GET /files/chunks/:hash/progress（C-6 / E-11 對齊後端實際詞彙）。
 * status: uploading（還有 chunk 沒 PUT 完）→ processing（全部進 MinIO，worker 在編碼／上雲）
 *         → completed（worker 全部完成）| failed（worker 至少一個 chunk 放棄）。
 * chunks 是 PUT 階段的計數；worker 是 worker 端的處理計數（worker 還不知道這個檔時缺席）。
 */
// UploadProgressStatus is generated from Go files.AllUploadStatuses() (ADR-043 G-2).

interface UploadProgressResponse {
  file_hash: string;
  total_chunks: number;
  chunks: {
    pending: number;
    completed: number;
    failed: number;
  };
  progress_percentage: number;
  status: UploadProgressStatus | string;
  worker?: {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    failed_indices?: number[];
    status: string;
  };
}

// ============== Constants ==============

const DEFAULT_CHUNK_SIZE = 50 * 1024 * 1024; // 50MB (reduced for browser stability)
const DEFAULT_MAX_CONCURRENT = 2;
// ADR-031 PR-C: presign in batches of 50 (backend cap 100) — halves the
// per-chunk API round-trips that dominated the 7.9s/chunk bottleneck.
const PRESIGN_BATCH_SIZE = 50;
// Cached presigned URLs are refreshed well within their 2h server-side TTL.
const PRESIGN_URL_MAX_AGE_MS = 90 * 60 * 1000;
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;
const POLL_INTERVAL_MS = 3000;

// QA #57 (2026-09-16): on a slow link (Chrome "Slow 3G" ≈ 400 kbps) a 50 MB
// chunk needs ~20 minutes, twice that with two chunks in flight, so the flat
// 10-minute XHR timeout killed every chunk and three retries later the upload
// "failed" with a network error. A chunk is now timed out on *stall* only —
// no upload progress for UPLOAD_STALL_TIMEOUT_MS — so a transfer that keeps
// moving is never cut, whatever its speed, and a dead connection is still
// retried within minutes rather than never.
const UPLOAD_STALL_TIMEOUT_MS = 3 * 60 * 1000;
// The control-plane calls (init / presign / chunk-complete / complete /
// progress) share the link with the chunk PUTs; the client's default 30 s
// trips while the pipe is saturated. chunk/complete additionally waits for
// the worker to accept the chunk (backend worker.timeout is 10 minutes).
const CONTROL_TIMEOUT_MS = 5 * 60 * 1000;
const CHUNK_COMPLETE_TIMEOUT_MS = 10 * 60 * 1000;

// ADR-054 D2: a chunked upload that loses the network pauses instead of
// failing: it waits for the browser's `online` event or the next 30-second
// probe, for at most the resume window (UPLOAD_RESUME_WINDOW, default 4h),
// then refreshes the login and carries on from the chunks still missing.
export const DEFAULT_UPLOAD_RESUME_WINDOW_MS = 4 * 60 * 60 * 1000;
const RESUME_PROBE_INTERVAL_MS = 30 * 1000;

/** 4h →「4 小時」, 90m →「90 分鐘」: the resume window as the dialog states it. */
export function formatResumeWindow(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60000));
  return minutes % 60 === 0 ? `${minutes / 60} 小時` : `${minutes} 分鐘`;
}

/** A chunk PUT that never got an answer: the connection failed or stalled. */
class UploadNetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadNetworkError';
  }
}

// Gateway answers (ingress / nginx while the API is unreachable or rolling)
// are an outage too, not a decision about this upload.
const GATEWAY_STATUSES = new Set([502, 503, 504]);

// isConnectivityError: the request never reached a server that answered —
// the network is down (or a gateway says the API is). Narrower than
// isNetworkError below on purpose: only these pause the upload; a server
// that answered 4xx/5xx, a cancel, or a bug still fails as before.
function isConnectivityError(err: unknown): boolean {
  if (!err || typeof err !== 'object' || isSessionExpiredError(err)) return false;
  if (err instanceof UploadNetworkError) return true;
  const status = (err as { status?: unknown }).status;
  if (typeof status === 'number') return GATEWAY_STATUSES.has(status);
  // api.ts hands skipGlobalError callers an Error wrapping the AxiosError.
  // (AxiosError marks itself with isAxiosError; checked structurally so this
  // file keeps to the shared client.)
  const original = ((err as { originalError?: unknown }).originalError ?? err) as {
    isAxiosError?: boolean;
    code?: string;
    response?: { status?: number };
  };
  if (!original || original.isAxiosError !== true || original.code === 'ERR_CANCELED') return false;
  return !original.response || GATEWAY_STATUSES.has(original.response.status ?? 0);
}

/** The window elapsed with the network still down: the upload has failed (reported as usual). */
function resumeWindowElapsedError(windowMs: number): Error {
  return new Error(`網路中斷超過 ${formatResumeWindow(windowMs)}未恢復，上傳未完成`);
}

/** The login ended while the upload was paused: stop at once, like any session-expired error (QA #102). */
function sessionEndedError(code: string | null): Error {
  return Object.assign(new Error('登入已逾時，請重新登入'), { status: 401, code: code ?? 'UNAUTHORIZED' });
}

interface NetworkGate {
  /** Whether outages are waited out at all (resume window > 0). */
  readonly enabled: boolean;
  /**
   * Wait until the network and the login are back. Every caller that hits an
   * outage shares one pause (and one deadline, kept across flapping until a
   * step succeeds). Rejects when the window elapses, the user cancels, or
   * the server says the session is over.
   */
  wait(): Promise<void>;
  /** A step succeeded: the outage, if any, is over. */
  resumed(): void;
}

function createNetworkGate(
  windowMs: number,
  signal: AbortSignal | undefined,
  onPauseChange: ((pause: UploadPause | null) => void) | undefined,
): NetworkGate {
  let pause: UploadPause | null = null;
  let waiting: Promise<void> | null = null;

  // One wake-up: the next probe tick, the browser coming back online, the
  // deadline, or a cancel — whichever is first.
  const nap = (ms: number) =>
    new Promise<void>((resolve) => {
      const done = () => {
        window.clearTimeout(timer);
        window.removeEventListener('online', done);
        signal?.removeEventListener('abort', done);
        resolve();
      };
      const timer = window.setTimeout(done, ms);
      window.addEventListener('online', done);
      signal?.addEventListener('abort', done);
    });

  const waitUntilBack = async (deadline: number): Promise<void> => {
    for (;;) {
      if (signal?.aborted) throw new Error('Upload aborted');
      await nap(Math.max(0, Math.min(RESUME_PROBE_INTERVAL_MS, deadline - Date.now())));
      if (signal?.aborted) throw new Error('Upload aborted');
      if (Date.now() >= deadline) throw resumeWindowElapsedError(windowMs);
      if (typeof navigator !== 'undefined' && navigator.onLine === false) continue;
      // Probe = the normal refresh: it needs the network and the session, and
      // the fresh access token is what the resumed requests need anyway. The
      // server keeps the session for an upload in progress (ADR-054 D1).
      if (!localStorage.getItem('refresh_token')) throw sessionEndedError(null);
      if (await refreshAuthToken()) return;
      const terminal = lastRefreshTerminalCode();
      if (terminal) throw sessionEndedError(terminal);
      // No answer (still offline) or a transient server error: keep waiting.
    }
  };

  return {
    enabled: windowMs > 0,
    wait() {
      if (!waiting) {
        if (!pause) {
          const now = Date.now();
          pause = { since: now, deadline: now + windowMs };
          console.warn(`[upload] network lost; waiting up to ${formatResumeWindow(windowMs)} to resume`);
          onPauseChange?.(pause);
        }
        waiting = waitUntilBack(pause.deadline).finally(() => {
          waiting = null;
        });
      }
      return waiting;
    },
    resumed() {
      if (pause) {
        pause = null;
        console.log('[upload] network back; upload resumed');
        onPauseChange?.(null);
      }
    },
  };
}

// isNetworkError: axios gave up without a server response (connection
// dropped, timed out) — worth retrying; a 4xx/5xx is a decision, not noise.
// QA #102: an expired login session (401 after the silent refresh) is never
// retried — every retry would 401 the same way.
function isNetworkError(err: unknown): boolean {
  if (isSessionExpiredError(err)) return false;
  const e = err as { response?: unknown; code?: string } | undefined;
  return !!e && typeof e === 'object' && !e.response && e.code !== 'ERR_CANCELED';
}

// retryOnNetworkError runs fn up to MAX_RETRIES times, backing off 1s / 2s,
// and only for network errors; anything else propagates at once. With a
// gate (ADR-054), an outage that outlasts the retries pauses on the gate and
// the retries start over once it is back, instead of failing.
async function retryOnNetworkError<T>(
  what: string,
  fn: () => Promise<T>,
  signal?: AbortSignal,
  gate?: NetworkGate,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (signal?.aborted) throw new Error('Upload aborted');
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1)));
    }
    try {
      const result = await fn();
      gate?.resumed();
      return result;
    } catch (err) {
      if (!isNetworkError(err)) throw err;
      lastError = err;
      console.warn(`[upload] ${what} attempt ${attempt + 1} hit a network error, retrying:`, err);
      if (attempt === MAX_RETRIES - 1 && gate?.enabled && isConnectivityError(err)) {
        await gate.wait();
        attempt = -1; // back online: a fresh set of retries
      }
    }
  }
  throw lastError;
}

// Progress weights
const HASH_WEIGHT = 10;
const UPLOAD_WEIGHT = 40;
const PROCESSING_WEIGHT = 50;

// ============== Utility Functions ==============

/**
 * Calculate both file hash and all chunk hashes in a single pass.
 * Reads the file only ONCE -- eliminates redundant I/O.
 * Memory usage: ~hashChunkSize (adaptive, 32-64MB) regardless of file size.
 */
async function calculateAllHashes(
  file: File,
  chunkSize: number,
  onProgress?: (progress: number) => void
): Promise<HashResult> {
  const { hashChunkSize } = getAdaptiveConfig();
  const totalChunks = Math.ceil(file.size / chunkSize);
  const chunkHashes: string[] = [];

  // File-level incremental hasher -- updates across all chunks
  const fileHasher = await createSHA256();
  fileHasher.init();

  let bytesProcessed = 0;

  for (let i = 0; i < totalChunks; i++) {
    const chunkStart = i * chunkSize;
    const chunkEnd = Math.min(chunkStart + chunkSize, file.size);

    // Chunk-level hasher -- resets per chunk
    const chunkHasher = await createSHA256();
    chunkHasher.init();

    // Read this chunk in sub-chunks (hashChunkSize) to limit memory
    for (let offset = chunkStart; offset < chunkEnd; offset += hashChunkSize) {
      const subEnd = Math.min(offset + hashChunkSize, chunkEnd);
      const slice = file.slice(offset, subEnd);
      const buffer = await slice.arrayBuffer();
      const data = new Uint8Array(buffer);

      // Feed into both hashers simultaneously
      fileHasher.update(data);
      chunkHasher.update(data);

      bytesProcessed += data.byteLength;
      if (onProgress) {
        onProgress((bytesProcessed / file.size) * 100);
      }
    }

    chunkHashes.push(chunkHasher.digest('hex'));
  }

  const fileHash = fileHasher.digest('hex');

  return { fileHash, chunkHashes };
}

/**
 * Upload a single chunk to a presigned URL using XMLHttpRequest.
 * Uses XHR instead of fetch to support upload progress events.
 */
function uploadChunkToPresignedUrl(
  presignedUrl: string,
  chunkData: Blob,
  signal?: AbortSignal,
  onProgress?: (loaded: number, total: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('PUT', presignedUrl, true);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');

    // Stall watchdog (QA #57): re-armed on every progress event, so only a
    // connection that stops moving for UPLOAD_STALL_TIMEOUT_MS is aborted.
    // No absolute deadline — a slow link legitimately takes as long as it
    // takes. After the last byte the same window covers the server's reply.
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    let stalled = false;
    const armStallTimer = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        stalled = true;
        xhr.abort();
      }, UPLOAD_STALL_TIMEOUT_MS);
    };
    const disarmStallTimer = () => {
      if (stallTimer) clearTimeout(stallTimer);
    };

    xhr.upload.onprogress = (event) => {
      armStallTimer();
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded, event.total);
      }
    };

    xhr.onload = () => {
      disarmStallTimer();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(Object.assign(new Error(`上傳儲存端回應 HTTP ${xhr.status}`), { status: xhr.status }));
      }
    };

    // ADR-054: no answer at all (connection failed or stalled) is an outage,
    // which pauses the upload rather than failing it.
    xhr.onerror = () => {
      disarmStallTimer();
      reject(new UploadNetworkError('上傳連線中斷（網路錯誤）'));
    };

    xhr.onabort = () => {
      disarmStallTimer();
      reject(stalled
        ? new UploadNetworkError(`上傳停滯超過 ${UPLOAD_STALL_TIMEOUT_MS / 60000} 分鐘沒有進度，已中斷`)
        : new Error('Upload aborted'));
    };

    // Handle abort signal
    if (signal) {
      if (signal.aborted) {
        reject(new Error('Upload aborted'));
        return;
      }
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new Error('Upload aborted'));
      });
    }

    armStallTimer();
    xhr.send(chunkData);
  });
}

/**
 * Simple semaphore for limiting concurrent operations.
 */
class Semaphore {
  private queue: Array<() => void> = [];
  private running = 0;

  constructor(private readonly maxConcurrent: number) {}

  async acquire(): Promise<void> {
    if (this.running < this.maxConcurrent) {
      this.running++;
      return;
    }
    return new Promise<void>((resolve) => {
      this.queue.push(() => {
        this.running++;
        resolve();
      });
    });
  }

  release(): void {
    this.running--;
    const next = this.queue.shift();
    if (next) {
      next();
    }
  }
}

// ============== Service ==============

const directUploadService = {
  /**
   * Initialize a direct upload session
   */
  async initUpload(
    fileHash: string,
    fileName: string,
    fileSize: number,
    chunkSize: number
  ): Promise<InitUploadResponse> {
    const response = await api.post<ApiResponse<InitUploadResponse>>(
      '/api/v1/files/upload/init',
      {
        file_hash: fileHash,
        file_name: fileName,
        file_size: fileSize,
        chunk_size: chunkSize,
      },
      { skipGlobalError: true, timeout: CONTROL_TIMEOUT_MS }
    );
    return response.data.data!;
  },

  /**
   * Get a presigned URL for uploading a specific chunk
   */
  async getPresignedUrl(
    fileHash: string,
    chunkIndex: number
  ): Promise<PresignedUrlResponse> {
    const response = await api.post<ApiResponse<PresignedUrlResponse>>(
      `/api/v1/files/upload/chunk/presign`,
      { file_hash: fileHash, chunk_index: chunkIndex },
      { skipGlobalError: true, timeout: CONTROL_TIMEOUT_MS }
    );
    return response.data.data!;
  },

  /**
   * Get presigned URLs for many chunks in one round-trip (ADR-031 PR-C).
   * The single-chunk endpoint above is kept for fallback/compatibility.
   */
  async getPresignedUrlsBatch(
    fileHash: string,
    chunkIndexes: number[]
  ): Promise<PresignedUrlBatchResponse> {
    const response = await api.post<ApiResponse<PresignedUrlBatchResponse>>(
      `/api/v1/files/upload/chunk/presign-batch`,
      { file_hash: fileHash, chunk_indexes: chunkIndexes },
      { skipGlobalError: true, timeout: CONTROL_TIMEOUT_MS }
    );
    return response.data.data!;
  },

  /**
   * Notify backend that a chunk has been uploaded to MinIO
   */
  async notifyChunkComplete(
    fileHash: string,
    chunkIndex: number,
    chunkHash: string,
    chunkSize: number,
    fileName: string,
    fileSize: number,
    totalChunks: number
  ): Promise<ChunkCompleteResponse> {
    const response = await api.post<ApiResponse<ChunkCompleteResponse>>(
      `/api/v1/files/upload/chunk/complete`,
      {
        file_hash: fileHash,
        chunk_index: chunkIndex,
        chunk_hash: chunkHash,
        chunk_size: chunkSize,
        file_name: fileName,
        file_size: fileSize,
        total_chunks: totalChunks,
      },
      { skipGlobalError: true, timeout: CHUNK_COMPLETE_TIMEOUT_MS }
    );
    return response.data.data!;
  },

  /**
   * Poll upload processing progress
   */
  async getProgress(fileHash: string, totalChunks: number): Promise<UploadProgressResponse> {
    const response = await api.get<ApiResponse<UploadProgressResponse>>(
      `/api/v1/files/chunks/${fileHash}/progress?total_chunks=${totalChunks}`,
      { skipGlobalError: true, timeout: CONTROL_TIMEOUT_MS }
    );
    return response.data.data!;
  },
};

// ============== Main Upload Function ==============

/**
 * Upload a file directly to MinIO via presigned URLs.
 *
 * Flow:
 *   1. Calculate file hash + per-chunk hashes in single pass
 *   2. Init upload session via backend API
 *   3. For each chunk: get presigned URL, PUT to MinIO, notify backend
 *   4. Poll progress until processing completes
 */
export async function directUpload(
  file: File,
  options: DirectUploadOptions = {}
): Promise<DirectUploadResult> {
  const {
    chunkSize = DEFAULT_CHUNK_SIZE,
    maxConcurrent = DEFAULT_MAX_CONCURRENT,
    onProgress,
    signal,
    resumeWindowMs = DEFAULT_UPLOAD_RESUME_WINDOW_MS,
    onPauseChange,
  } = options;

  // N-1（產品決策 2026-09-08）：0-byte 檔案不備份。Files.tsx 在選檔時就擋下；
  // 這裡再擋一次，避免其他呼叫端送出一個後端必回 FILE_EMPTY 的 init。
  if (file.size === 0) {
    throw new Error('空檔案（0 byte）不備份');
  }

  const startTime = Date.now();
  let bytesUploaded = 0;
  let uploadingChunks = 0;
  const completedChunkSet = new Set<number>();
  const totalChunks = Math.ceil(file.size / chunkSize);

  // ADR-031 PR-C: proactively refresh the JWT every 20h while this upload
  // runs, so a multi-day transfer never dies to the 24h token expiry (the
  // 401 interceptor covers the reactive path; this avoids relying on it).
  const stopTokenKeepalive = startTokenKeepalive();
  // ADR-054 D2: the idle timer leaves the user signed in while this runs,
  // paused or not.
  const endUploadActivity = beginUploadActivity();
  const gate = createNetworkGate(Math.max(0, resumeWindowMs), signal, onPauseChange);

  // Helper to check abort
  const checkAbort = () => {
    if (signal?.aborted) {
      throw new Error('Upload aborted');
    }
  };

  // Helper to calculate overall progress based on phase weights
  const calcOverallProgress = (
    phase: DirectUploadProgress['phase'],
    phaseProgress: number
  ): number => {
    switch (phase) {
      case 'hashing':
        return (phaseProgress / 100) * HASH_WEIGHT;
      case 'initializing':
        return HASH_WEIGHT;
      case 'uploading':
        return HASH_WEIGHT + (phaseProgress / 100) * UPLOAD_WEIGHT;
      case 'processing':
        return HASH_WEIGHT + UPLOAD_WEIGHT + (phaseProgress / 100) * PROCESSING_WEIGHT;
      case 'completed':
        return 100;
      case 'failed':
        return HASH_WEIGHT + UPLOAD_WEIGHT + (phaseProgress / 100) * PROCESSING_WEIGHT;
      default:
        return 0;
    }
  };

  // Helper to report progress
  const reportProgress = (
    phase: DirectUploadProgress['phase'],
    phaseProgress: number,
    extra?: Partial<Pick<DirectUploadProgress, 'uploadProgress' | 'processingProgress' | 'hashProgress'>>
  ) => {
    if (!onProgress) return;

    const overallProgress = Math.round(calcOverallProgress(phase, phaseProgress) * 10) / 10;
    const elapsed = (Date.now() - startTime) / 1000;
    const speed = elapsed > 0 ? bytesUploaded / elapsed : 0;

    const progress: DirectUploadProgress = {
      phase,
      overallProgress,
      ...extra,
    };

    if (phase === 'hashing') {
      progress.hashProgress = phaseProgress;
    }

    if (phase === 'uploading') {
      const remainingBytes = file.size - bytesUploaded;
      const eta = speed > 0 ? remainingBytes / speed : undefined;
      progress.uploadProgress = {
        totalChunks,
        uploadedChunks: completedChunkSet.size,
        uploadingChunks,
        bytesUploaded,
        speed,
        eta,
      };
    }

    onProgress(progress);
  };

  try {
    // Phase 1: Calculate hashes
    reportProgress('hashing', 0);
    checkAbort();

    const { fileHash, chunkHashes } = await calculateAllHashes(file, chunkSize, (p) => {
      reportProgress('hashing', p);
    });

    checkAbort();

    // Phase 2: Initialize upload
    reportProgress('initializing', 0);

    const initResponse = await retryOnNetworkError(
      'init',
      () => directUploadService.initUpload(fileHash, file.name, file.size, chunkSize),
      signal,
    );

    checkAbort();

    // Handle dedup -- file already exists
    if (initResponse.dedup_status === 'same_name_same_hash') {
      reportProgress('completed', 100);
      return {
        fileHash,
        fileName: file.name,
        fileSize: file.size,
        totalChunks: initResponse.total_chunks,
        status: 'deduplicated',
      };
    }

    // Handle name conflict
    if (initResponse.dedup_status === 'same_name_diff_hash') {
      throw new Error('File with same name but different content already exists');
    }

    // Phase 3: Upload chunks via presigned URLs with concurrency control
    // INFRA-ADR-023 D7 / SRS-008 FR-5.2: IndexedDB resume state.
    // Init or resume state; skip chunks already in completedChunks.
    let resumeState: { completedChunks: number[] } = { completedChunks: [] };
    try {
      const s = await initUploadState(
        fileHash,
        file.name,
        file.size,
        totalChunks,
        chunkSize,
      );
      resumeState = { completedChunks: s.completedChunks };
      if (s.completedChunks.length > 0) {
        // Pre-populate in-memory set so progress bar reflects resume
        s.completedChunks.forEach((i) => completedChunkSet.add(i));
        console.log(
          `[upload-resume] resuming ${file.name}: ${s.completedChunks.length}/${totalChunks} chunks already done`,
        );
      }
    } catch (err) {
      // IndexedDB failure is non-fatal — upload proceeds without resume
      console.warn('[upload-resume] IndexedDB init failed, resume disabled:', err);
    }

    reportProgress('uploading', 0);

    const semaphore = new Semaphore(maxConcurrent);
    const alreadyDone = new Set(resumeState.completedChunks);
    const chunkIndices = Array.from({ length: totalChunks }, (_, i) => i).filter(
      (i) => !alreadyDone.has(i),
    );

    // ADR-031 PR-C: batch presign pool. URLs are fetched 50 at a time and
    // consumed as chunks start; when the pool runs dry the next chunk
    // triggers a single-flight refill. A per-chunk fallback to the single
    // presign endpoint keeps retries and older backends working.
    const urlPool = new Map<number, { url: string; fetchedAt: number }>();
    let batchFetchPromise: Promise<void> | null = null;

    const refillUrlPool = (wantedIndex: number): Promise<void> => {
      if (!batchFetchPromise) {
        batchFetchPromise = (async () => {
          const pending = chunkIndices.filter(
            (i) => i !== wantedIndex && !completedChunkSet.has(i) && !urlPool.has(i),
          );
          const want = [wantedIndex, ...pending].slice(0, PRESIGN_BATCH_SIZE);
          const resp = await directUploadService.getPresignedUrlsBatch(fileHash, want);
          const now = Date.now();
          for (const item of resp.urls) {
            urlPool.set(item.chunk_index, { url: item.presigned_url, fetchedAt: now });
          }
        })().finally(() => {
          batchFetchPromise = null;
        });
      }
      return batchFetchPromise;
    };

    const acquirePresignedUrl = async (chunkIndex: number): Promise<string> => {
      const cached = urlPool.get(chunkIndex);
      if (cached) {
        urlPool.delete(chunkIndex); // consume-once; a retry re-fetches fresh
        if (Date.now() - cached.fetchedAt < PRESIGN_URL_MAX_AGE_MS) {
          return cached.url;
        }
      }
      try {
        await refillUrlPool(chunkIndex);
        const refilled = urlPool.get(chunkIndex);
        if (refilled) {
          urlPool.delete(chunkIndex);
          return refilled.url;
        }
      } catch (err) {
        // QA #102: the single presign would 401 the same way.
        if (isSessionExpiredError(err)) throw err;
        console.warn('[upload] batch presign failed, falling back to single presign:', err);
      }
      // Fallback: single presign (also covers a chunk missing from the batch).
      const single = await directUploadService.getPresignedUrl(fileHash, chunkIndex);
      return single.presigned_url;
    };

    const uploadChunkWithRetry = async (chunkIndex: number): Promise<void> => {
      await semaphore.acquire();
      try {
        checkAbort();
        uploadingChunks++;

        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunkData = file.slice(start, end);
        const actualChunkSize = end - start;

        let lastError: Error | null = null;

        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          try {
            if (attempt > 0) {
              // Exponential backoff: 1s, 2s, 4s
              await new Promise(r => setTimeout(r, RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1)));
            }

            checkAbort();

            // Get presigned URL (batch pool with single-presign fallback)
            const presignedUrl = await acquirePresignedUrl(chunkIndex);

            checkAbort();

            // PUT chunk data directly to MinIO via presigned URL
            await uploadChunkToPresignedUrl(presignedUrl, chunkData, signal, (loaded) => {
              // Per-chunk XHR progress -- update bytes uploaded estimate
              const chunkBytesUploaded = loaded;
              const totalUploaded = Array.from(completedChunkSet).reduce((sum, idx) => {
                const s = idx * chunkSize;
                const e = Math.min(s + chunkSize, file.size);
                return sum + (e - s);
              }, 0) + chunkBytesUploaded;

              bytesUploaded = totalUploaded;
              const uploadPercent = (completedChunkSet.size / totalChunks) * 100;
              reportProgress('uploading', uploadPercent);
            });

            checkAbort();

            // Notify backend that chunk is uploaded
            await directUploadService.notifyChunkComplete(
              fileHash,
              chunkIndex,
              chunkHashes[chunkIndex],
              actualChunkSize,
              file.name,
              file.size,
              totalChunks
            );

            completedChunkSet.add(chunkIndex);
            uploadingChunks--;

            // INFRA-ADR-023 D7: persist completion to IndexedDB for resume
            markChunkCompleteDB(fileHash, chunkIndex).catch((e) => {
              // Non-fatal; resume state will just be slightly behind reality
              console.warn('[upload-resume] markChunkComplete failed:', e);
            });

            // Update progress after successful upload
            bytesUploaded = Array.from(completedChunkSet).reduce((sum, idx) => {
              const s = idx * chunkSize;
              const e = Math.min(s + chunkSize, file.size);
              return sum + (e - s);
            }, 0);

            const uploadPercent = (completedChunkSet.size / totalChunks) * 100;
            reportProgress('uploading', uploadPercent);

            gate.resumed(); // ADR-054: a chunk got through, the outage (if any) is over
            return; // Success
          } catch (err) {
            lastError = err as Error;
            if ((err as Error).message === 'Upload aborted') {
              throw err;
            }
            // QA #102: login session gone — stop now instead of retrying
            // three times; the caller tells the user to log in again.
            if (isSessionExpiredError(err)) {
              uploadingChunks--;
              throw err;
            }
            console.warn(`Chunk ${chunkIndex} attempt ${attempt + 1} failed:`, err);
            // ADR-054 D2: the network is gone, not the upload. Once the quick
            // retries are spent, pause until it is back (or the window ends —
            // gate.wait() then rejects and the upload fails as before), drop
            // the presigned URLs fetched before the outage, and start this
            // chunk over with fresh retries. Chunks already completed stay
            // done; the ones queued behind the semaphore follow in order.
            if (attempt === MAX_RETRIES - 1 && gate.enabled && isConnectivityError(err)) {
              try {
                await gate.wait();
              } catch (waitErr) {
                uploadingChunks--;
                throw waitErr;
              }
              urlPool.clear();
              attempt = -1;
            }
          }
        }

        uploadingChunks--;
        throw new Error(
          `第 ${chunkIndex + 1}/${totalChunks} 塊上傳失敗，已自動重試 ${MAX_RETRIES} 次` +
            (lastError ? `：${lastError.message}` : ''),
        );
      } finally {
        semaphore.release();
      }
    };

    // Launch all chunk uploads -- semaphore controls concurrency
    await Promise.all(chunkIndices.map(uploadChunkWithRetry));

    // Phase 4: Poll processing progress until completed or failed
    reportProgress('processing', 0);

    const result = await pollProcessingProgress(fileHash, totalChunks, signal, gate, (progressData) => {
      const percent = progressData.progress_percentage || 0;

      reportProgress('processing', percent, {
        processingProgress: {
          totalChunks: progressData.total_chunks,
          completedChunks: progressData.worker?.completed ?? progressData.chunks?.completed ?? 0,
          failedChunks: progressData.worker?.failed ?? progressData.chunks?.failed ?? 0,
          failedChunkIndices: progressData.worker?.failed_indices ?? [],
        },
      });
    });

    if (result.status === 'failed' || result.chunks?.failed > 0) {
      const failedIndices = result.worker?.failed_indices ?? [];
      reportProgress('failed', 0, {
        processingProgress: {
          totalChunks: result.total_chunks,
          completedChunks: result.worker?.completed ?? result.chunks?.completed ?? 0,
          failedChunks: result.worker?.failed ?? result.chunks?.failed ?? 0,
          failedChunkIndices: failedIndices,
        },
      });
      const n = result.worker?.failed ?? result.chunks?.failed ?? 0;
      throw new Error(
        `Upload processing failed: ${n} chunk(s) failed` +
          (failedIndices.length ? ` (chunks ${failedIndices.join(', ')})` : ''),
      );
    }

    // Phase 5: Finalize — create files table record so UI shows the file
    try {
      await retryOnNetworkError('finalize', () => api.post<ApiResponse<{ file_id: string; status: string }>>(
        `/api/v1/files/upload/complete`,
        {
          file_hash: fileHash,
          file_name: file.name,
          file_path: file.name,
          file_size: file.size,
          total_chunks: totalChunks,
          backup_source: 'manual',
        },
        { signal, timeout: CHUNK_COMPLETE_TIMEOUT_MS }
      ), signal, gate);
    } catch (err) {
      // Non-fatal: chunks already in MinIO and Worker will still process them.
      console.warn('Failed to finalize upload (file record not created):', err);
    }

    // INFRA-ADR-023 D7: clean up IndexedDB resume state after finalize
    removeUploadState(fileHash).catch((e) => {
      console.warn('[upload-resume] removeUploadState failed:', e);
    });

    reportProgress('completed', 100);

    return {
      fileHash,
      fileName: file.name,
      fileSize: file.size,
      totalChunks,
      status: result.status,
    };
  } catch (error) {
    if (signal?.aborted) {
      throw new Error('Upload aborted by user');
    }
    throw error;
  } finally {
    stopTokenKeepalive();
    endUploadActivity();
    gate.resumed(); // never leave the dialog saying「網路中斷」after the upload ended
  }
}

/**
 * Poll the backend for processing progress until completed or failed.
 */
async function pollProcessingProgress(
  fileHash: string,
  totalChunks: number,
  signal: AbortSignal | undefined,
  gate: NetworkGate,
  onProgress?: (data: UploadProgressResponse) => void
): Promise<UploadProgressResponse> {
  for (;;) {
    if (signal?.aborted) {
      throw new Error('Upload aborted');
    }

    // ADR-054: an outage while waiting for the worker's verdict pauses too.
    const progress = await retryOnNetworkError(
      'progress',
      () => directUploadService.getProgress(fileHash, totalChunks),
      signal,
      gate,
    );

    if (onProgress) {
      onProgress(progress);
    }

    // 所有 chunk 進 MinIO 後後端回 processing（worker 接手，可能要跑很久：
    // 256 GB 的 RS 編碼＋三雲上傳）；上傳對話框到此為止，之後的狀態在檔案
    // 清單裡看（uploading → ready / error）。completed / failed 是 worker
    // 已經給出結論的情況，直接帶回。
    if (progress.status !== 'uploading' || progress.chunks?.failed > 0) {
      return progress;
    }

    // Wait before next poll
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(resolve, POLL_INTERVAL_MS);
      if (signal) {
        signal.addEventListener('abort', () => {
          clearTimeout(timeout);
          reject(new Error('Upload aborted'));
        }, { once: true });
      }
    });
  }
}

